// src/components/shared/Header.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, X, Home, Bike,
  LogOut, User, LayoutDashboard,
  ChevronDown, Settings, HelpCircle, Shield,
  CreditCard, History, Star
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/Button';
import Logo from './Logo';
import { useAuth } from '../../hooks/useAuth';
import NotificationBell from './NotificationBell';

interface HeaderProps {}

const Header: React.FC<HeaderProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setShowUserMenu(false);
  }, [location]);

  // Get dashboard URL based on user role
  const getDashboardUrl = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'rider': return '/dashboard/rider';
      case 'customer': return '/dashboard/customer';
      case 'admin': return '/admin';
      case 'support': return '/support';
      default: return '/choose-role';
    }
  };

  // Get user dashboard actions based on role
  const getDashboardActions = () => {
    if (!user) return [];
    
    if (user.role === 'rider') {
      return [
        { label: 'My Orders', icon: <Bike className="h-4 w-4" />, path: '/rider/orders' },
        { label: 'Earnings', icon: <CreditCard className="h-4 w-4" />, path: '/rider/earnings' },
        { label: 'History', icon: <History className="h-4 w-4" />, path: '/rider/history' },
        { label: 'Profile', icon: <User className="h-4 w-4" />, path: '/rider/profile' },
      ];
    } else if (user.role === 'customer') {
      return [
        { label: 'My Orders', icon: <Bike className="h-4 w-4" />, path: '/customer/orders' },
        { label: 'Payments', icon: <CreditCard className="h-4 w-4" />, path: '/customer/payments' },
        { label: 'History', icon: <History className="h-4 w-4" />, path: '/customer/history' },
        { label: 'Profile', icon: <User className="h-4 w-4" />, path: '/customer/profile' },
      ];
    }
    return [];
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
    setShowUserMenu(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 shadow-2xl py-2' 
          : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/" className="flex items-center gap-2">
              <Logo size="lg" showText={true} animated={true} />
            </Link>
          </motion.div>

          {/* Desktop Navigation - Only Home and Marketplace */}
          <nav className="hidden lg:flex items-center gap-6">
            {/* Home */}
            <Link
              to="/"
              className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-emerald-600 ${
                location.pathname === '/' 
                  ? 'text-emerald-600' 
                  : 'text-gray-700'
              }`}
            >
              <Home className="h-4 w-4" />
              Home
            </Link>

            {/* Marketplace */}
            <Link
              to="/marketplace"
              className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-emerald-600 ${
                location.pathname === '/marketplace' 
                  ? 'text-emerald-600' 
                  : 'text-gray-700'
              }`}
            >
              <Bike className="h-4 w-4" />
              Marketplace
            </Link>
          </nav>

          {/* Right Section - Notifications, Dashboard Button, User Menu */}
          <div className="flex items-center gap-2">
            {/* Notifications - Only for authenticated users */}
            {isAuthenticated && <NotificationBell />}

            {/* Dashboard Button - Visible for authenticated users (hidden on mobile) */}
            {isAuthenticated && (
              <motion.button
                onClick={() => navigate(getDashboardUrl())}
                className="hidden lg:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-emerald-500/25 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <LayoutDashboard className="h-4 w-4" />
                <span>Dashboard</span>
              </motion.button>
            )}

            {/* User Menu / Auth Buttons (Desktop) */}
            <div className="hidden lg:block">
              {isAuthenticated ? (
                <div className="relative">
                  <motion.button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-gray-100 transition-colors"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold text-sm">
                      {user.fullName?.charAt(0) || user.email?.charAt(0) || 'U'}
                    </div>
                    <span className="text-sm font-medium text-gray-700 hidden xl:block">
                      {user.fullName?.split(' ')[0] || 'User'}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-300 ${showUserMenu ? 'rotate-180' : ''}`} />
                  </motion.button>

                  <AnimatePresence>
                    {showUserMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-50"
                      >
                        {/* User Info */}
                        <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 border-b border-gray-200">
                          <p className="font-semibold text-gray-900">{user.fullName || 'User'}</p>
                          <p className="text-xs text-gray-600 mt-1">{user.email}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full capitalize">
                              {user.role}
                            </span>
                            {user.rating ? (
                              <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-1">
                                <Star className="h-3 w-3" />
                                {user.rating.toFixed(1)}
                              </span>
                            ) : null}
                          </div>
                        </div>

                        {/* Dashboard Link */}
                        <Link
                          to={getDashboardUrl()}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-200"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <LayoutDashboard className="h-4 w-4 text-emerald-600" />
                          <span className="text-sm font-medium text-gray-700">Dashboard</span>
                        </Link>

                        {/* Role-specific actions */}
                        {getDashboardActions().map((action, index) => (
                          <Link
                            key={index}
                            to={action.path}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                            onClick={() => setShowUserMenu(false)}
                          >
                            {action.icon}
                            <span className="text-sm text-gray-700">{action.label}</span>
                          </Link>
                        ))}

                        {/* Settings */}
                        <Link
                          to={user.role === 'rider' ? '/rider/profile' : '/customer/profile'}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-t border-gray-200"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Settings className="h-4 w-4 text-gray-600" />
                          <span className="text-sm text-gray-700">Settings</span>
                        </Link>

                        {/* Safety Center */}
                        <Link
                          to={user.role === 'rider' ? '/rider/safety' : '/customer/safety'}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Shield className="h-4 w-4 text-gray-600" />
                          <span className="text-sm text-gray-700">Safety Center</span>
                        </Link>

                        {/* Logout */}
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors border-t border-gray-200 text-red-600"
                        >
                          <LogOut className="h-4 w-4" />
                          <span className="text-sm font-medium">Logout</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    className="text-gray-700 hover:text-gray-900"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={() => navigate('/signup')}
                    variant="carryngo"
                    className="px-6"
                  >
                    Get Started
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button - Always visible on mobile */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors relative z-50"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Enhanced with better animations and positioning */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden fixed inset-x-0 top-[72px] bottom-0 bg-white/95 backdrop-blur-xl overflow-y-auto z-40"
            >
              <div className="container mx-auto px-4 py-6">
                <div className="space-y-6">
                  {/* Navigation Links */}
                  <div className="space-y-2">
                    {/* Home */}
                    <Link
                      to="/"
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center gap-3 w-full px-4 py-4 rounded-xl transition-all ${
                        location.pathname === '/'
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <Home className="h-5 w-5" />
                      <span className="font-medium text-lg">Home</span>
                    </Link>

                    {/* Marketplace */}
                    <Link
                      to="/marketplace"
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center gap-3 w-full px-4 py-4 rounded-xl transition-all ${
                        location.pathname === '/marketplace'
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <Bike className="h-5 w-5" />
                      <span className="font-medium text-lg">Marketplace</span>
                    </Link>
                  </div>

                  {/* User Profile Section */}
                  {isAuthenticated ? (
                    <div className="border-t border-gray-200 pt-6 mt-4">
                      {/* User Info */}
                      <div className="flex items-center gap-4 px-4 py-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl mb-4">
                        <div className="h-14 w-14 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold text-xl">
                          {user.fullName?.charAt(0) || user.email?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-lg">{user.fullName || 'User'}</p>
                          <p className="text-sm text-gray-600">{user.email}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full capitalize font-medium">
                              {user.role}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Dashboard Link */}
                      <Link
                        to={getDashboardUrl()}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 w-full px-4 py-4 rounded-xl hover:bg-gray-50 text-gray-700 transition-all"
                      >
                        <LayoutDashboard className="h-5 w-5 text-emerald-600" />
                        <span className="font-medium text-lg">Dashboard</span>
                      </Link>

                      {/* Role-specific actions */}
                      {getDashboardActions().map((action, index) => (
                        <Link
                          key={index}
                          to={action.path}
                          onClick={() => setIsMenuOpen(false)}
                          className="flex items-center gap-3 w-full px-4 py-4 rounded-xl hover:bg-gray-50 text-gray-700 transition-all"
                        >
                          {action.icon}
                          <span className="font-medium text-lg">{action.label}</span>
                        </Link>
                      ))}

                      {/* Settings */}
                      <Link
                        to={user.role === 'rider' ? '/rider/profile' : '/customer/profile'}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 w-full px-4 py-4 rounded-xl hover:bg-gray-50 text-gray-700 transition-all border-t border-gray-200 mt-4 pt-4"
                      >
                        <Settings className="h-5 w-5 text-gray-600" />
                        <span className="font-medium text-lg">Settings</span>
                      </Link>

                      {/* Safety Center */}
                      <Link
                        to={user.role === 'rider' ? '/rider/safety' : '/customer/safety'}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-3 w-full px-4 py-4 rounded-xl hover:bg-gray-50 text-gray-700 transition-all"
                      >
                        <Shield className="h-5 w-5 text-gray-600" />
                        <span className="font-medium text-lg">Safety Center</span>
                      </Link>

                      {/* Logout */}
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-4 rounded-xl text-red-600 hover:bg-red-50 transition-all mt-4"
                      >
                        <LogOut className="h-5 w-5" />
                        <span className="font-medium text-lg">Logout</span>
                      </button>
                    </div>
                  ) : (
                    <div className="border-t border-gray-200 pt-6 mt-4 space-y-3">
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigate('/login');
                          setIsMenuOpen(false);
                        }}
                        className="w-full py-4 text-lg"
                      >
                        Sign In
                      </Button>
                      <Button
                        onClick={() => {
                          navigate('/signup');
                          setIsMenuOpen(false);
                        }}
                        variant="carryngo"
                        className="w-full py-4 text-lg"
                      >
                        Create Account
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;